/* __next_internal_client_entry_do_not_use__ default auto */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// Global
import { useCallback, useEffect, useRef, useState } from 'react';
import { tv } from 'tailwind-variants';
import { useRouter } from 'next/navigation';
import { withStandardComponentWrapper } from 'helpers/HOC';
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import { getTestProps } from 'lib/testing/utils';
import { SvgIcon } from 'helpers/SvgIcon';
import { getCtaStyle } from 'lib/utils/cta-utils';
import { parseStyleParams } from 'lib/utils/style-param-utils';
import { useStableSerialized } from 'lib/hooks/useStableSerialized';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';
import useIsEditing from 'lib/hooks/useIsEditing';
/**
 * Multilist may arrive as `targetItems` (Edge/GraphQL) or `jsonValue[]`
 * (Design Library / REST). Kept colocated with the client component (only
 * consumer) so we don't create a circular `LocationsBlock` ↔
 * `LocationsBlock.client` import that breaks the App Router server/client
 * module boundary.
 */ function resolveOfficeLocationsList(fields) {
    if (!fields) return [];
    const nested = fields.data?.item?.officeLocations;
    const flat = fields.officeLocations;
    const raw = nested ?? flat;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    const obj = raw;
    if (Array.isArray(obj.targetItems)) return obj.targetItems;
    if (Array.isArray(obj.jsonValue)) return obj.jsonValue;
    return [];
}
const LocationsBlockClient = (props)=>{
    const isEditing = useIsEditing();
    const { uid } = props?.rendering || {};
    const styles = parseStyleParams(props.params, [
        'cta1'
    ]);
    const item = props?.fields?.data?.item;
    const { image, searchPlaceholderText } = item || {};
    const router = useRouter();
    const officeList = resolveOfficeLocationsList(props.fields);
    // CMS office list identity can change reference every render; stabilize so we do not
    // `setState` in a loop (infinite logs / blank UI on Design Library routes).
    const stableLocationsKey = useStableSerialized(officeList.map((i)=>({
            id: i.id,
            lat: i.latitude?.jsonValue?.value,
            lon: i.longitude?.jsonValue?.value
        })));
    /** User postcode sort overrides default CMS order; cleared when CMS list content changes. */ const [sortedOrder, setSortedOrder] = useState(null);
    useEffect(()=>{
        setSortedOrder(null);
    }, [
        stableLocationsKey
    ]);
    const targetItemsRef = useRef(officeList);
    targetItemsRef.current = officeList;
    const locationsForDisplay = sortedOrder ?? officeList;
    const [searchText, setSearchText] = useState('');
    const [searchError, setSearchError] = useState('');
    // Haversine distance in km
    const distanceKm = (lat1, lon1, lat2, lon2)=>{
        const toRad = (deg)=>deg * Math.PI / 180;
        const R = 6371; // km
        const dLat = toRad(lat2 - lat1);
        const dLon = toRad(lon2 - lon1);
        const sinDLat = Math.sin(dLat / 2);
        const sinDLon = Math.sin(dLon / 2);
        const a = sinDLat * sinDLat + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * sinDLon * sinDLon;
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    };
    // Handle Enter key or button click: use stateful searchText to fetch and redirect
    const handleSearchKeyDown = useCallback(async (e)=>{
        const isKeyboardEvent = 'key' in e;
        const isEnter = isKeyboardEvent && e.key === 'Enter';
        const isClick = !isKeyboardEvent && e.type === 'click';
        if (!isEnter && !isClick) return;
        e.preventDefault();
        const value = searchText.trim();
        if (!value) {
            setSearchError('Please enter a valid postcode');
            return;
        }
        const params = new URLSearchParams({
            lookup: value
        });
        try {
            const res = await fetch('/api/postcode/PostCode', {
                method: 'POST',
                body: params
            });
            const json = await res.json();
            if (!res.ok) {
                setSearchError(json.error);
                return;
            }
            const first = json?.Item?.[0];
            const latStr = first?.Latitude;
            const lonStr = first?.Longitude;
            if (!latStr || !lonStr) return;
            const userLat = parseFloat(latStr);
            const userLon = parseFloat(lonStr);
            if (Number.isNaN(userLat) || Number.isNaN(userLon)) return;
            const withDistance = (targetItemsRef.current || []).map((loc)=>{
                const olat = parseFloat(loc?.latitude?.jsonValue?.value);
                const olon = parseFloat(loc?.longitude?.jsonValue?.value);
                if (Number.isNaN(olat) || Number.isNaN(olon)) return null;
                const dist = distanceKm(userLat, userLon, olat, olon);
                return {
                    loc,
                    dist
                };
            }).filter((x)=>!!x);
            if (withDistance.length === 0) return;
            withDistance.sort((a, b)=>a.dist - b.dist);
            const ordered = withDistance.map((x)=>x.loc);
            setSortedOrder(ordered);
            const nearest = withDistance[0]?.loc;
            const href = nearest?.url?.url;
            if (href) router.push(href);
        } catch  {
        // silently ignore errors
        }
    }, [
        router,
        searchText
    ]);
    const { base, sectionContentWrapper, searchContainer, searchField, searchInputPlaceholder, listWrapper, linkLine, imageContainer, imageStyle, goSearchIconWrapper, resetSearchIconWrapper, errorMessageWrapperStyles, errorMessageStyles } = TAILWIND_VARIANTS({
        state: searchError ? 'error' : 'default'
    });
    return /*#__PURE__*/ _jsx("section", {
        "data-component": "authorable/shared/lists/locationsblock",
        ...editingHydrationProps(isEditing),
        id: uid,
        ...getTestProps(`component-locations-block-${uid}`),
        children: /*#__PURE__*/ _jsx("div", {
            className: base(),
            children: /*#__PURE__*/ _jsxs("div", {
                className: sectionContentWrapper(),
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: searchContainer(),
                        ...getTestProps('search-container'),
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: searchField(),
                                role: "search",
                                children: [
                                    /*#__PURE__*/ _jsx(SvgIcon, {
                                        icon: "outline-search",
                                        viewBox: "0 0 20 20",
                                        size: "s"
                                    }),
                                    /*#__PURE__*/ _jsx("input", {
                                        type: "search",
                                        id: "search_offices",
                                        onChange: (e)=>setSearchText(e.target.value),
                                        onKeyDown: handleSearchKeyDown,
                                        autoComplete: "off",
                                        placeholder: searchPlaceholderText?.jsonValue?.value,
                                        "aria-label": "Search by postcode",
                                        className: searchInputPlaceholder() + ' flex-1 bg-transparent outline-none',
                                        ...getTestProps('search-input'),
                                        value: searchText
                                    }),
                                    searchText && /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            /*#__PURE__*/ _jsx("button", {
                                                className: resetSearchIconWrapper(),
                                                "aria-label": "Clear search",
                                                role: "button",
                                                type: "button",
                                                onClick: ()=>setSearchText(''),
                                                children: /*#__PURE__*/ _jsx(SvgIcon, {
                                                    size: "xs",
                                                    fill: "currentColor",
                                                    viewBox: "4 3 24 24",
                                                    icon: "menu-close"
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx("button", {
                                                id: "SearchResultButton",
                                                type: "button",
                                                className: goSearchIconWrapper(),
                                                role: "button",
                                                "aria-label": "Search",
                                                onClick: handleSearchKeyDown,
                                                children: /*#__PURE__*/ _jsx(SvgIcon, {
                                                    size: "xs",
                                                    fill: "currentColor",
                                                    icon: "arrow-dash-right"
                                                })
                                            })
                                        ]
                                    })
                                ]
                            }),
                            searchError && /*#__PURE__*/ _jsxs("div", {
                                className: errorMessageWrapperStyles(),
                                children: [
                                    /*#__PURE__*/ _jsx(SvgIcon, {
                                        icon: "form-error",
                                        className: 'text-color-text-text-critical',
                                        size: "xs",
                                        viewBox: "0 0 16 17"
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: errorMessageStyles(),
                                        children: searchError
                                    })
                                ]
                            })
                        ]
                    }),
                    locationsForDisplay.length > 0 ? /*#__PURE__*/ _jsx("ul", {
                        className: listWrapper(),
                        children: locationsForDisplay.map((link)=>{
                            const linkField = {
                                text: link?.officeDisplayName?.jsonValue?.value,
                                href: link?.url?.url
                            };
                            return /*#__PURE__*/ _jsx("li", {
                                children: /*#__PURE__*/ _jsx(LinkWrapper, {
                                    ctaComponentClass: "component-card-item-button-1-color",
                                    className: linkLine(),
                                    ctaStyle: getCtaStyle(styles.cta1, 'tertiary'),
                                    field: linkField,
                                    ...getTestProps(`location-link`),
                                    suppressNewTabIcon: true,
                                    ctaIcon: "arrow-dash-right"
                                })
                            }, link?.id);
                        })
                    }) : null,
                    /*#__PURE__*/ _jsx("div", {
                        className: imageContainer(),
                        children: image?.jsonValue?.value?.src ? /*#__PURE__*/ _jsx(ImageWrapper, {
                            field: image?.jsonValue,
                            className: imageStyle(),
                            ...getTestProps(`image`)
                        }) : /*#__PURE__*/ _jsx("div", {
                            ...getTestProps(`no-image`),
                            children: /*#__PURE__*/ _jsx(ImageWrapper, {
                                field: {
                                    value: {
                                        src: '/assets/profile_placeholder.png',
                                        alt: 'No image'
                                    }
                                },
                                className: imageStyle(),
                                ...getTestProps(`related-profile-image-placeholder`)
                            })
                        })
                    })
                ]
            })
        })
    });
};
export default withStandardComponentWrapper(LocationsBlockClient);
const TAILWIND_VARIANTS = tv({
    slots: {
        base: [
            'flex',
            'bg-component-section-bg',
            'gap-component-section-container-gutter',
            'flex-wrap',
            'relative'
        ],
        sectionContentWrapper: [
            'flex',
            'flex-col',
            'w-full',
            'h-auto'
        ],
        searchContainer: [
            'flex',
            'flex-col',
            'items-center',
            'justify-center',
            'pb-padding-more',
            'pt-0',
            'gap-spacing-spacing-12'
        ],
        searchField: [
            'flex',
            'items-center',
            'gap-spacing-spacing-8',
            'h-[48px]',
            'px-spacing-spacing-8',
            'rounded-border-radius-radius-2',
            'border',
            'border-color-input-border',
            'bg-color-grayscale-white',
            'w-full',
            'max-w-[606px]',
            'hover:border-color-input-border-hover',
            'focus-within:outline-none',
            'focus-within:ring-1',
            'focus-within:!border-color-input-border-focused',
            'focus-within:ring-color-input-border-focused',
            'focus-within:ring-shadow-[0_0_0_1px_var(--color-core-color-4-SkyBlue)]'
        ],
        searchInputPlaceholder: [
            'text-color-core-color-1-Navy',
            'font-typography-font-family-body',
            'text-typography-font-size-body-medium',
            'leading-typography-line-height-body-medium',
            'placeholder:italic',
            'placeholder:text-color-grayscale-600',
            '[&::-webkit-search-cancel-button]:hidden'
        ],
        listWrapper: [
            'grid',
            'grid-cols-1',
            'md:grid-cols-2',
            'lg:grid-cols-3',
            'gap-x-layout-base-margin-x',
            'gap-y-layout-base-gutter',
            'pt-padding-tight',
            'pb-padding-more',
            'text-left'
        ],
        linkLine: [
            '!font-normal',
            'justify-between'
        ],
        imageContainer: [
            'relative',
            'rounded-border-radius-radius-4',
            'overflow-hidden'
        ],
        imageStyle: [
            'w-full',
            'h-full',
            'object-cover',
            'aspect-square',
            'md:aspect-[1.55/1]',
            'lg:aspect-[160/47]'
        ],
        goSearchIconWrapper: [
            'rounded-general-border-radius-button',
            'bg-component-button-primary-bg',
            'border-component-button-primary-border',
            'border-general-border-width-button',
            'text-component-button-primary-icon',
            'p-[7px]',
            'disabled:no-underline',
            'disabled:opacity-50',
            'focus:outline-none',
            'focus:ring-4',
            'focus:ring-offset-0',
            'focus:ring-color-core-color-4-SkyBlue',
            'hover:no-underline',
            'hover:bg-component-button-primary-bg-hover',
            'hover:border-component-button-primary-border-hover',
            'hover:text-component-button-primary-icon-hover',
            'focus:bg-component-button-primary-bg-focused',
            'focus:border-component-button-primary-border-focused',
            'focus:text-component-button-primary-icon-focused',
            'disabled:bg-component-button-primary-bg-disabled',
            'disabled:border-component-button-primary-border-disabled',
            'disabled:text-component-button-primary-icon-disabled'
        ],
        resetSearchIconWrapper: [
            'rounded-general-border-radius-button',
            'border-component-button-secondary-border',
            'border-general-border-width-button',
            'bg-component-button-secondary-bg',
            'text-component-button-secondary-icon',
            'p-[7px]',
            'disabled:no-underline',
            'disabled:opacity-50',
            'focus:outline-none',
            'focus:ring-4',
            'focus:ring-offset-0',
            'focus:ring-color-core-color-4-SkyBlue',
            'hover:no-underline',
            'hover:bg-component-button-secondary-bg-hover',
            'hover:border-component-button-secondary-bg-hover',
            'hover:text-component-button-secondary-icon-hover',
            'focus:bg-component-button-secondary-bg-focused',
            'focus:border-component-button-secondary-bg-focused',
            'focus:text-component-button-secondary-icon-focused',
            'disabled:bg-component-button-secondary-bg-disabled',
            'disabled:border-component-button-secondary-border-disabled',
            'disabled:text-component-button-secondary-icon-disabled'
        ],
        errorMessageWrapperStyles: [
            'flex',
            'gap-spacing-spacing-4',
            'items-center',
            'w-full',
            'max-w-[606px]'
        ],
        errorMessageStyles: [
            'text-color-text-text-critical',
            'font-typography-font-family-body',
            'text-typography-font-size-body-small',
            'leading-[21px]',
            'font-normal',
            'text-left'
        ]
    },
    defaultVariants: {
        state: 'default'
    },
    variants: {
        state: {
            default: {
                searchField: []
            },
            error: {
                searchField: [
                    'border-color-text-text-critical',
                    'focus:border-color-text-text-error'
                ]
            }
        }
    }
});
